import React, { useEffect, useState } from 'react';
import Layout from '@/components/Layout';
import ModuleIntelligenceBlock from '@/components/ModuleIntelligenceBlock';
import { api } from '@/utils/api';
import {
  TrendingUp, Activity, MapPin, ShieldCheck, Sparkles, Calculator,
  BarChart3, Loader2, ExternalLink, Building2, Target, ArrowRight,
} from 'lucide-react';
import { toast } from 'sonner';

// ─────────────────────────────────────────────────────────────────────────────
// Saudi Real Estate Intelligence Dashboard
// Route: /intelligence
//
// Tabs:
//   • Market dashboard (heat map + headline KPIs)
//   • Opportunities (district scoring)
//   • Valuation (form → estimate)
//   • Rental pricing (form → recommendation)
//   • Comparables (form → similar units)
//
// Every response surfaces the official data-source attribution per user spec:
//   "Data feed from srem.moj.gov.sa & rega.gov.sa".
// ─────────────────────────────────────────────────────────────────────────────

const TYPES = ['apartment', 'villa', 'townhouse', 'duplex', 'office', 'retail', 'warehouse', 'land', 'hospitality'];
const CONDITIONS = ['new', 'excellent', 'good', 'fair', 'poor'];
const AMENITIES = ['pool', 'gym', 'smart_home', 'parking', 'garden', 'balcony', 'concierge', 'security', 'view', 'near_metro', 'near_school', 'near_mall'];

export default function Intelligence() {
  const [tab, setTab] = useState('market');
  const [districts, setDistricts] = useState([]);

  useEffect(() => {
    (async () => {
      try { const { data } = await api.intelligenceDistricts(); setDistricts(data.districts || []); }
      catch { /* silent */ }
    })();
  }, []);

  return (
    <Layout>
      <div className="max-w-[1280px] mx-auto px-6 py-8" data-testid="intelligence-page">
        <div className="flex items-start justify-between gap-6 mb-8 flex-wrap">
          <div>
            <div className="inline-flex items-center gap-2 px-2.5 py-1 rounded-full bg-[#0E1320] text-white text-[10.5px] font-bold tracking-[0.18em] reva-mono uppercase">
              <Sparkles className="h-3 w-3" /> Saudi Market Intelligence
            </div>
            <h1 className="mt-3 text-3xl md:text-4xl font-semibold tracking-tight text-[#0E1320]">REVA understands the market, not only the property.</h1>
            <p className="mt-1.5 text-[14px] text-[#4A5263] max-w-3xl">
              Valuation · rental pricing · opportunity detection · comparable matching · executive market dashboard. Anchored on official Saudi sources.
            </p>
          </div>
          <DataSourceBadge />
        </div>

        {/* Tabs */}
        <div className="flex gap-1 border-b border-[#E6EAF0] mb-6 overflow-x-auto" role="tablist">
          {[
            { id: 'market',         label: 'Market dashboard',    icon: BarChart3 },
            { id: 'opportunities',  label: 'Opportunities',        icon: TrendingUp },
            { id: 'valuation',      label: 'Property valuation',   icon: Calculator },
            { id: 'rental',         label: 'Rental pricing',       icon: Activity },
            { id: 'comparables',    label: 'Comparables',          icon: Target },
          ].map(t => (
            <button key={t.id} onClick={() => setTab(t.id)} data-testid={`intelligence-tab-${t.id}`}
              className={`inline-flex items-center gap-2 px-4 py-2.5 text-[13px] font-medium transition-colors border-b-2 whitespace-nowrap ${
                tab === t.id ? 'border-[#00734A] text-[#0E1320]' : 'border-transparent text-[#4A5263] hover:text-[#0E1320]'
              }`}>
              <t.icon className="h-3.5 w-3.5" /> {t.label}
            </button>
          ))}
        </div>

        {tab === 'market'        && <MarketTab />}
        {tab === 'opportunities' && <OpportunitiesTab />}
        {tab === 'valuation'     && <ValuationTab districts={districts} />}
        {tab === 'rental'        && <RentalTab districts={districts} />}
        {tab === 'comparables'   && <ComparablesTab districts={districts} />}

        <div className="mt-8">
          <ModuleIntelligenceBlock module="intelligence" testId="intelligence-intel" />
        </div>
      </div>
    </Layout>
  );
}

// ─── Data-source attribution band ───────────────────────────────────────
function DataSourceBadge() {
  return (
    <div className="rounded-2xl border border-emerald-200 bg-emerald-50/60 px-4 py-3 max-w-md" data-testid="intelligence-data-sources">
      <div className="inline-flex items-center gap-2 text-[10.5px] reva-mono uppercase tracking-[0.18em] text-emerald-800">
        <ShieldCheck className="h-3 w-3" /> Official sources
      </div>
      <div className="mt-1 text-[12px] text-emerald-900 leading-relaxed">
        Anchored on{' '}
        <a className="underline" href="https://srem.moj.gov.sa/" target="_blank" rel="noreferrer">srem.moj.gov.sa</a>{' '}
        (Saudi Real Estate Exchange · MoJ) and{' '}
        <a className="underline" href="https://rega.gov.sa/" target="_blank" rel="noreferrer">rega.gov.sa</a>{' '}
        (Real Estate General Authority).
      </div>
    </div>
  );
}

// ─── Market dashboard tab ───────────────────────────────────────────────
function MarketTab() {
  const [d, setD] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try { const { data } = await api.intelligenceMarket(); setD(data); }
      catch (e) { toast.error('Market load failed'); }
      finally { setLoading(false); }
    })();
  }, []);

  if (loading) return <div className="text-center py-12 text-[#71777E]"><Loader2 className="h-5 w-5 animate-spin mx-auto" /></div>;
  if (!d) return null;

  return (
    <div data-testid="intelligence-market-tab">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
        <KPI label="Avg 12m growth" val={`${d.headline.avg_growth_12m_pct}%`} icon={TrendingUp} />
        <KPI label="Avg yield" val={`${d.headline.avg_yield_pct}%`} icon={Activity} />
        <KPI label="Avg vacancy" val={`${d.headline.avg_vacancy_pct}%`} icon={MapPin} accent="text-amber-600" />
        <KPI label="Districts tracked" val={d.headline.districts_tracked} icon={Building2} />
      </div>

      <div className="grid md:grid-cols-2 gap-3 mb-6">
        <DistrictRanking title="Fastest-growing districts" rows={d.top_growth_districts} field="growth_12m" suffix="%" />
        <DistrictRanking title="Highest yields" rows={d.top_yield_districts} field="yield" suffix="%" />
      </div>

      <h3 className="text-[14px] font-semibold text-[#0E1320] mb-3">District heat map · SAR / m² (sale)</h3>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-2 mb-4">
        {d.heatmap.map(h => {
          const intensity = Math.min(1, h.avg_sqm_sale / 9500);
          const bg = `rgba(0, 115, 74, ${0.10 + intensity * 0.55})`;
          return (
            <div key={h.id} className="rounded-xl p-3 text-white" style={{ background: bg }} data-testid={`intelligence-heat-${h.id}`}>
              <div className="text-[10.5px] reva-mono uppercase tracking-[0.16em] opacity-80">{h.city}</div>
              <div className="mt-1 text-[14px] font-semibold text-white drop-shadow">{h.name_ar}</div>
              <div className="mt-2 text-[13px] reva-mono">{h.avg_sqm_sale.toLocaleString()} SAR/m²</div>
              <div className="text-[11px] opacity-90 reva-mono">Yield {h.yield}% · Growth {h.growth_12m}%</div>
            </div>
          );
        })}
      </div>
      <p className="text-[11px] text-[#71777E] reva-mono">Data anchored on srem.moj.gov.sa · rega.gov.sa</p>
    </div>
  );
}

function DistrictRanking({ title, rows, field, suffix }) {
  return (
    <div className="rounded-xl border border-[#E6EAF0] bg-white">
      <div className="px-4 py-3 border-b border-[#F1F4F7]">
        <h3 className="text-[13px] font-semibold text-[#0E1320]">{title}</h3>
      </div>
      <ul className="divide-y divide-[#F1F4F7]">
        {rows.map((r, i) => (
          <li key={r.id} className="px-4 py-2.5 flex items-center justify-between text-[12.5px]">
            <span className="flex items-center gap-2">
              <span className="w-5 h-5 rounded-full bg-[#F1F4F7] text-[#0E1320] text-[10px] font-bold flex items-center justify-center">{i + 1}</span>
              <span className="text-[#0E1320] font-medium">{r.name_ar}</span>
              <span className="text-[#71777E]">· {r.city}</span>
            </span>
            <span className="reva-mono text-[#0E1320] font-semibold">{r[field]}{suffix}</span>
          </li>
        ))}
      </ul>
    </div>
  );
}

// ─── Opportunities tab ──────────────────────────────────────────────────
function OpportunitiesTab() {
  const [rows, setRows] = useState([]);
  useEffect(() => {
    (async () => {
      try { const { data } = await api.intelligenceOpportunities(); setRows(data.opportunities || []); }
      catch (e) { toast.error('Opportunities load failed'); }
    })();
  }, []);
  return (
    <div data-testid="intelligence-opportunities-tab" className="rounded-xl border border-[#E6EAF0] bg-white overflow-hidden">
      <table className="w-full text-[13px]">
        <thead className="bg-[#FBFCFD] text-[10.5px] reva-mono uppercase tracking-[0.16em] text-[#71777E]">
          <tr>
            <th className="text-start px-4 py-3 font-medium">District</th>
            <th className="text-start px-4 py-3 font-medium">Score</th>
            <th className="text-start px-4 py-3 font-medium">Signals</th>
            <th className="text-start px-4 py-3 font-medium">Yield</th>
            <th className="text-start px-4 py-3 font-medium">Risk</th>
            <th className="text-start px-4 py-3 font-medium">Action</th>
          </tr>
        </thead>
        <tbody>
          {rows.map(r => (
            <tr key={r.district_id} className="border-t border-[#F1F4F7]" data-testid={`opportunity-row-${r.district_id}`}>
              <td className="px-4 py-3 text-[#0E1320] font-medium">{r.district.name_ar} <span className="text-[11px] text-[#71777E] ms-1 reva-mono">{r.district.city}</span></td>
              <td className="px-4 py-3">
                <span className={`px-2 py-0.5 rounded-full text-[11px] font-semibold reva-mono ${
                  r.opportunity_score >= 80 ? 'bg-emerald-100 text-emerald-800' :
                  r.opportunity_score >= 60 ? 'bg-amber-100 text-amber-800' :
                  'bg-[#F1F4F7] text-[#4A5263]'
                }`}>{r.opportunity_score}</span>
              </td>
              <td className="px-4 py-3 text-[12px] text-[#4A5263]">
                <ul className="space-y-0.5">
                  {r.growth_signal.map((s, i) => <li key={i}>· {s}</li>)}
                </ul>
              </td>
              <td className="px-4 py-3 text-[#0E1320] reva-mono">{r.yield_estimate_pct}%</td>
              <td className="px-4 py-3 text-[12px]">
                <span className={`px-2 py-0.5 rounded-full text-[11px] font-medium ${
                  r.risk_alert === 'low' ? 'bg-emerald-50 text-emerald-700' :
                  r.risk_alert === 'medium' ? 'bg-amber-50 text-amber-800' :
                  'bg-red-50 text-red-700'
                }`}>{r.risk_alert}</span>
              </td>
              <td className="px-4 py-3 text-[12px] text-[#4A5263]">{r.recommended_action}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// ─── Valuation tab ──────────────────────────────────────────────────────
function ValuationTab({ districts }) {
  const [form, setForm] = useState({ district_id: districts[0]?.id || '', property_type: 'apartment', size_sqm: 200, condition: 'good', age_years: 5, amenities: [] });
  const [result, setResult] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => { if (districts.length && !form.district_id) setForm(f => ({ ...f, district_id: districts[0].id })); }, [districts]); // eslint-disable-line

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try { const { data } = await api.intelligenceValuation(form); setResult(data); }
    catch (e2) { toast.error(e2?.response?.data?.detail || 'Valuation failed'); }
    finally { setBusy(false); }
  };

  return (
    <div className="grid md:grid-cols-[420px_1fr] gap-6" data-testid="intelligence-valuation-tab">
      <PropertyForm form={form} setForm={setForm} districts={districts} busy={busy} onSubmit={submit} cta="Estimate value" testid="valuation" />
      {result ? <ValuationResult r={result} /> : <EmptyHint title="Estimate property market value" desc="Fill in the form. REVA returns a SAR estimate with low/mid/high band, confidence score, comparable benchmarks, and risk notes — anchored on srem.moj.gov.sa benchmarks." />}
    </div>
  );
}

function ValuationResult({ r }) {
  return (
    <div className="rounded-2xl border border-[#E6EAF0] bg-white p-6" data-testid="valuation-result">
      <div className="flex items-baseline gap-2">
        <span className="text-[10.5px] reva-mono uppercase tracking-[0.18em] text-[#71777E]">Estimated market value</span>
        <span className="ms-auto px-2 py-0.5 rounded-full bg-emerald-50 text-emerald-700 text-[11px] font-medium reva-mono">Confidence {r.confidence_score}%</span>
      </div>
      <div className="mt-2 text-4xl font-semibold text-[#0E1320]">{r.estimate_sar.toLocaleString()} <span className="text-[16px] text-[#4A5263] font-normal ms-1">SAR</span></div>
      <div className="mt-1 text-[12px] text-[#4A5263] reva-mono">Range: {r.range.low.toLocaleString()} — {r.range.high.toLocaleString()} SAR</div>

      <div className="mt-5 grid grid-cols-2 gap-3 text-[12px]">
        {Object.entries(r.explanation).filter(([k]) => k !== 'formula').map(([k, v]) => (
          <div key={k} className="rounded-lg bg-[#FBFCFD] border border-[#F1F4F7] px-3 py-2">
            <div className="text-[10.5px] reva-mono uppercase tracking-[0.16em] text-[#71777E]">{k.replace(/_/g, ' ')}</div>
            <div className="mt-1 text-[#0E1320] font-medium reva-mono">{typeof v === 'number' ? v.toLocaleString() : String(v)}</div>
          </div>
        ))}
      </div>

      {r.risk_notes.length > 0 && (
        <div className="mt-5 rounded-xl bg-amber-50 border border-amber-200 p-4">
          <div className="text-[12.5px] font-semibold text-amber-900">Risk notes</div>
          <ul className="mt-2 list-disc list-inside text-[12px] text-amber-900 space-y-1">
            {r.risk_notes.map((n, i) => <li key={i}>{n}</li>)}
          </ul>
        </div>
      )}

      <DataAttribution />
    </div>
  );
}

// ─── Rental pricing tab ─────────────────────────────────────────────────
function RentalTab({ districts }) {
  const [form, setForm] = useState({ district_id: districts[0]?.id || '', property_type: 'apartment', size_sqm: 180, condition: 'good', age_years: 4, amenities: [], current_rent: 0 });
  const [result, setResult] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => { if (districts.length && !form.district_id) setForm(f => ({ ...f, district_id: districts[0].id })); }, [districts]); // eslint-disable-line

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const payload = { ...form, current_rent: form.current_rent || null };
      const { data } = await api.intelligenceRentalPricing(payload);
      setResult(data);
    } catch (e2) { toast.error(e2?.response?.data?.detail || 'Rental pricing failed'); }
    finally { setBusy(false); }
  };

  return (
    <div className="grid md:grid-cols-[420px_1fr] gap-6" data-testid="intelligence-rental-tab">
      <PropertyForm form={form} setForm={setForm} districts={districts} busy={busy} onSubmit={submit} cta="Recommend rent" testid="rental"
        extraField={
          <label className="block">
            <span className="text-[12px] text-[#4A5263]">Current rent (optional)</span>
            <input type="number" value={form.current_rent} onChange={(e) => setForm(f => ({ ...f, current_rent: Number(e.target.value) }))} data-testid="rental-current"
              className="mt-1 w-full h-10 px-3 rounded-xl border border-[#E6EAF0] bg-white text-[13.5px]" />
          </label>
        } />
      {result ? <RentalResult r={result} /> : <EmptyHint title="AI rental pricing" desc="Recommend a market-aware annual rent in SAR, with confidence score, pricing range, expected occupancy impact, revenue upside, and risk level — anchored on srem.moj.gov.sa + rega.gov.sa." />}
    </div>
  );
}

function RentalResult({ r }) {
  return (
    <div className="rounded-2xl border border-[#E6EAF0] bg-white p-6" data-testid="rental-result">
      <div className="flex items-baseline gap-2">
        <span className="text-[10.5px] reva-mono uppercase tracking-[0.18em] text-[#71777E]">Recommended annual rent</span>
        <span className="ms-auto px-2 py-0.5 rounded-full bg-emerald-50 text-emerald-700 text-[11px] font-medium reva-mono">Confidence {r.confidence_score}%</span>
      </div>
      <div className="mt-2 text-4xl font-semibold text-[#0E1320]">{r.recommended_rent_yearly_sar.toLocaleString()} <span className="text-[16px] text-[#4A5263] font-normal ms-1">SAR / year</span></div>
      <div className="mt-1 text-[12px] text-[#4A5263] reva-mono">Range {r.range.low.toLocaleString()} — {r.range.high.toLocaleString()} SAR</div>

      <div className="mt-5 grid grid-cols-2 gap-3">
        <Info label="Risk level"     value={r.risk_level} />
        <Info label="Revenue upside" value={r.revenue_upside_sar !== null ? `${r.revenue_upside_sar.toLocaleString()} SAR` : '—'} />
        <Info label="Occupancy impact" value={r.expected_occupancy_impact} colSpan />
      </div>

      <div className="mt-4 text-[12px] text-[#4A5263]">{r.explanation.formula}</div>
      <DataAttribution />
    </div>
  );
}

function Info({ label, value, colSpan }) {
  return (
    <div className={`rounded-lg bg-[#FBFCFD] border border-[#F1F4F7] px-3 py-2 ${colSpan ? 'col-span-2' : ''}`}>
      <div className="text-[10.5px] reva-mono uppercase tracking-[0.16em] text-[#71777E]">{label}</div>
      <div className="mt-1 text-[#0E1320] font-medium text-[13px]">{value}</div>
    </div>
  );
}

// ─── Comparables tab ────────────────────────────────────────────────────
function ComparablesTab({ districts }) {
  const [form, setForm] = useState({ district_id: districts[0]?.id || '', property_type: 'apartment', size_sqm: 200, condition: 'good', amenities: [], limit: 5 });
  const [result, setResult] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => { if (districts.length && !form.district_id) setForm(f => ({ ...f, district_id: districts[0].id })); }, [districts]); // eslint-disable-line

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try { const { data } = await api.intelligenceComparables(form); setResult(data); }
    catch (e2) { toast.error(e2?.response?.data?.detail || 'Comparable search failed'); }
    finally { setBusy(false); }
  };

  return (
    <div className="grid md:grid-cols-[420px_1fr] gap-6" data-testid="intelligence-comparables-tab">
      <PropertyForm form={form} setForm={setForm} districts={districts} busy={busy} onSubmit={submit} cta="Find comparables" testid="comparables" />
      {result ? (
        <div className="rounded-2xl border border-[#E6EAF0] bg-white p-6" data-testid="comparables-result">
          <div className="flex items-baseline gap-3">
            <h3 className="text-[15px] font-semibold text-[#0E1320]">{result.comparables_count} comparable units</h3>
            <span className="text-[12px] text-[#4A5263] reva-mono ms-auto">Suggested rent: {result.pricing_impact.suggested_rent_yearly_sar.toLocaleString()} SAR/yr</span>
          </div>
          <ul className="mt-4 divide-y divide-[#F1F4F7]">
            {result.comparables.map((c) => (
              <li key={c.id} className="py-3 flex items-start gap-3" data-testid={`comparable-${c.id}`}>
                <div className="w-12 text-end">
                  <div className="text-[16px] font-semibold text-emerald-700 reva-mono">{Math.round(c._similarity * 100)}%</div>
                  <div className="text-[10px] reva-mono text-[#71777E] uppercase tracking-[0.16em]">match</div>
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-[13px] font-semibold text-[#0E1320]">{c.unit_number || c.name || c.id.slice(0, 8)}</div>
                  <div className="text-[12px] text-[#4A5263]">{c._diff_explain}</div>
                </div>
              </li>
            ))}
            {result.comparables_count === 0 && <li className="py-3 text-[#71777E] text-[12.5px]">No similar units in your portfolio yet — add more units to enrich comparables.</li>}
          </ul>
          <DataAttribution />
        </div>
      ) : <EmptyHint title="Find comparable properties" desc="Weighted matching (location 40% · size 20% · condition 15% · type 15% · amenities 10%) ranks units in your portfolio against the target." />}
    </div>
  );
}

// ─── Shared form ────────────────────────────────────────────────────────
function PropertyForm({ form, setForm, districts, busy, onSubmit, cta, testid, extraField }) {
  const upd = (k) => (e) => setForm(f => ({ ...f, [k]: e.target.value }));
  const toggleAmenity = (a) => setForm(f => ({ ...f, amenities: f.amenities.includes(a) ? f.amenities.filter(x => x !== a) : [...f.amenities, a] }));
  return (
    <form onSubmit={onSubmit} className="rounded-2xl border border-[#E6EAF0] bg-white p-5 space-y-3" data-testid={`${testid}-form`}>
      <Select label="District" value={form.district_id} onChange={upd('district_id')} opts={districts.map(d => ({ k: d.id, v: `${d.name_ar} · ${d.city}` }))} testid={`${testid}-district`} />
      <div className="grid grid-cols-2 gap-3">
        <Select label="Property type" value={form.property_type} onChange={upd('property_type')} opts={TYPES.map(t => ({ k: t, v: t }))} testid={`${testid}-type`} />
        <Number label="Size (m²)" value={form.size_sqm} onChange={(v) => setForm(f => ({ ...f, size_sqm: v }))} testid={`${testid}-size`} />
      </div>
      <div className="grid grid-cols-2 gap-3">
        <Select label="Condition" value={form.condition} onChange={upd('condition')} opts={CONDITIONS.map(c => ({ k: c, v: c }))} testid={`${testid}-condition`} />
        <Number label="Age (years)" value={form.age_years} onChange={(v) => setForm(f => ({ ...f, age_years: v }))} testid={`${testid}-age`} />
      </div>
      {extraField}
      <div>
        <span className="text-[12px] text-[#4A5263]">Amenities</span>
        <div className="mt-1.5 flex flex-wrap gap-1.5">
          {AMENITIES.map(a => (
            <button key={a} type="button" onClick={() => toggleAmenity(a)} data-testid={`${testid}-amenity-${a}`}
              className={`px-2.5 py-1 rounded-full text-[11px] font-medium border transition-colors ${
                form.amenities.includes(a) ? 'bg-[#0E1320] text-white border-[#0E1320]' : 'bg-white text-[#0E1320] border-[#E6EAF0] hover:border-[#0E1320]'
              }`}>
              {a.replace(/_/g, ' ')}
            </button>
          ))}
        </div>
      </div>
      <button disabled={busy} type="submit" data-testid={`${testid}-submit`}
        className="w-full mt-2 inline-flex items-center justify-center gap-2 h-11 rounded-xl bg-[#00734A] hover:bg-[#005F3D] text-white font-semibold text-[14px] disabled:opacity-60">
        {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <>{cta} <ArrowRight className="h-4 w-4" /></>}
      </button>
    </form>
  );
}

function Select({ label, value, onChange, opts, testid }) {
  return (
    <label className="block">
      <span className="text-[12px] text-[#4A5263]">{label}</span>
      <select value={value} onChange={onChange} data-testid={testid}
        className="mt-1 w-full h-10 px-3 rounded-xl border border-[#E6EAF0] bg-white text-[13.5px]">
        {opts.map(o => <option key={o.k} value={o.k}>{o.v}</option>)}
      </select>
    </label>
  );
}

function Number({ label, value, onChange, testid }) {
  return (
    <label className="block">
      <span className="text-[12px] text-[#4A5263]">{label}</span>
      <input type="number" min={0} value={value} onChange={(e) => onChange(Number(e.target.value))} data-testid={testid}
        className="mt-1 w-full h-10 px-3 rounded-xl border border-[#E6EAF0] bg-white text-[13.5px]" />
    </label>
  );
}

function KPI({ label, val, icon: Icon, accent = 'text-[#00734A]' }) {
  return (
    <div className="rounded-xl border border-[#E6EAF0] bg-white p-4">
      <div className="flex items-center justify-between">
        <Icon className={`h-4 w-4 ${accent}`} strokeWidth={1.7} />
        <span className="text-[10px] reva-mono uppercase tracking-[0.16em] text-[#71777E]">{label}</span>
      </div>
      <div className="mt-3 text-2xl font-semibold text-[#0E1320]">{val}</div>
    </div>
  );
}

function EmptyHint({ title, desc }) {
  return (
    <div className="rounded-2xl border border-dashed border-[#E6EAF0] bg-white p-10 text-center" data-testid="intelligence-empty-hint">
      <Sparkles className="h-8 w-8 mx-auto text-[#00734A]" strokeWidth={1.4} />
      <h3 className="mt-3 text-[15px] font-semibold text-[#0E1320]">{title}</h3>
      <p className="mt-1 text-[12.5px] text-[#4A5263] max-w-md mx-auto">{desc}</p>
    </div>
  );
}

function DataAttribution() {
  return (
    <div className="mt-5 pt-4 border-t border-[#F1F4F7] flex items-center gap-2 text-[11px] text-[#71777E]" data-testid="result-data-attribution">
      <ShieldCheck className="h-3.5 w-3.5" />
      <span>Data feed from official sources:</span>
      <a className="underline hover:text-[#0E1320] inline-flex items-center gap-1" href="https://srem.moj.gov.sa/" target="_blank" rel="noreferrer">srem.moj.gov.sa <ExternalLink className="h-3 w-3" /></a>
      <span>·</span>
      <a className="underline hover:text-[#0E1320] inline-flex items-center gap-1" href="https://rega.gov.sa/" target="_blank" rel="noreferrer">rega.gov.sa <ExternalLink className="h-3 w-3" /></a>
    </div>
  );
}
